import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { AbstractModalContentComponent } from '@ibe.components/common/modal/abstract-modal-content.component';
import { ModalService, ScreenService } from '@ibe/services/ui';
import { Store } from '@ngrx/store';
import { Observable, fromEvent, combineLatest } from 'rxjs';
import { takeWhile, debounceTime, map, distinctUntilChanged, startWith, tap } from 'rxjs/operators';
import * as fromStore from '../../../store';
import { environment } from '@environments/environment';
import { DailyCost } from '@ibe.models/daily-cost.model';
import { BookedTrip, PassengersSegmentedCount } from '@ibe/models';
import { BookingStep } from '@ibe/models/ui';
import * as moment from 'moment';

import { INDRACalendarMonthlyCost } from '@ibe.models/calendar-monthly-cost.indra.model';
import { INDRACalendarPriceService } from '@ibe.services/calendar-price.indra.service';

import { INDRABFMBookingMonthlyLowestPriceRequestParam, 
		INDRABFMBookingMonthlyLowestPriceResponse } from '@ibe.models/calendar-price.indra.model.lib';
		
import { TRIP_TYPE_ONE_WAY_CODE, 
		TRIP_TYPE_ROUND_TRIP_CODE, 
		SESSION_VAR_KEY_MARKET,
		} from '@ibe.config';
import { INDRACurrencyService } from '@ibe.services/currency.indra.service';
import { INDRAPassengerTypeService } from '@ibe.services/passenger-type.indra.service';
import { INDRAIbeAdminService } from '@ibe.services/ibeadmin.indra.service';




@Component({
	selector: 'app-select-month-modal',
	templateUrl: './select-month-modal.indra.component.html',
	styleUrls: ['./select-month-modal.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class INDRASelectMonthModalComponent extends AbstractModalContentComponent implements OnInit, OnDestroy {

	isAlive = true;
	isLoading = true;

	currency: string;
	
	@Input()
	set month(data) {
		this._months = Object.assign({}, data);
	}

	dailyCosts;
	_months;
	months = [];

	private loading$: Observable<boolean> = this.store.select(fromStore.getUserLoading);
	
	isMobile$: Observable<boolean> = this.store.select(fromStore.isScreenMobile);
	isMobile: boolean = false;
	
	private resize$: Observable<number>;
	
	priceUnit$: Observable<string> = this.store.select(fromStore.getOptionsBarPriceUnit);
	priceUnit;
	
	booking_trip$: Observable<BookedTrip> = this.store.select(fromStore.getSelectedBookedTrip);
	bookingTrip;
	
	private dailyCosts$: Observable<Array<DailyCost[]>> = this.store.select(fromStore.getBookingStepsCostsPerDay);
	
	bookingSteps$: Observable<Array<BookingStep>> = this.store.select(fromStore.getSelectedBookingSteps);
	bookingSteps: Array<BookingStep>;
	
	currentStep$: Observable<number> = this.store.select(fromStore.getSelectedBookingCurrentStep);
	currentStep = 0;

	userInfo$: Observable<Object> = this.store.select(fromStore.getUserInfo);
	isLoggedIn: boolean;

	passengers$: Observable<PassengersSegmentedCount> = this.store.select(fromStore.getMiniIBEPassengers);
	paxType: string;

	constructor(
		protected modalService: ModalService,
		private screen: ScreenService,
		private store: Store<fromStore.State>, 
		private cdr: ChangeDetectorRef,
		private calendarPriceService: INDRACalendarPriceService,
		private currencyService: INDRACurrencyService,
		private passengerTypeService: INDRAPassengerTypeService,
		private ibeAdminService: INDRAIbeAdminService) {
		super(modalService);

		this.currency = this.currencyService.INDRA_getCurrencySymbol().unit;
		this.isMobile$
			.pipe(takeWhile(() => this.isAlive))
			.subscribe(value => {
				this.isMobile = value;
			});

		this.resize$ = fromEvent(window, 'resize')
			.pipe(
				debounceTime(250),
				map(() => window.innerWidth),
				distinctUntilChanged(),
				startWith(window.innerWidth),
				tap(width => this.screen.setWindowWidth(Number(width)))
		);

		this.priceUnit$.pipe(takeWhile(() => this.isAlive)).subscribe(value => {
			this.priceUnit = value;
		});


		this.booking_trip$.subscribe(value => {
			this.bookingTrip = value;
		});

		this.dailyCosts$
		.pipe(takeWhile(() => this.isAlive))
		.subscribe(costs => {
			if (costs) {
				this.dailyCosts = costs;
			}
		});
		this.bookingSteps$
		.pipe(takeWhile(() => this.isAlive))
		.subscribe(steps => {
			if (steps) {
				this.bookingSteps = steps;
			}
		});

		this.currentStep$
			.pipe(takeWhile(() => this.isAlive))
			.subscribe(value => {
				this.currentStep = value;
			});

		this.userInfo$
		.pipe(takeWhile(() => this.isAlive))
		.subscribe((user: any) => {
			this.isLoggedIn = user ? true : false;
		});

		this.calendarPriceService.monthlyLowestPrice$
			.pipe(takeWhile(() => this.isAlive))
			.subscribe(response => {
				this.months = response;
				this.cdr.markForCheck();
			});

		this.passengers$
		.pipe(takeWhile(() => this.isAlive))
		.subscribe(value => {
			this.paxType = this.passengerTypeService.getPassengerCodeByCount(value);
		});

		this.buildMonthlist();
	}

	INDRA_printMinPrice(valueDisplay): string {
		if (valueDisplay) {
			if (this.priceUnit === 'miles') {
				return this.INDRA_getTranslationKey('dates.text.from') + this.currencyService.INDRA_formatMiles(valueDisplay) + ' M';
			}
			else if (this.priceUnit === 'cash') {
				return this.INDRA_getTranslationKey('dates.text.from') + this.currencyService.INDRA_formatCash(valueDisplay) + ' ' +  this.currency;
			}
		}

		return '';
	}
	
	INDRA_monthHasMinPrice(valueDisplay) {
		return (valueDisplay ? true : false);
	}

	ngOnInit() {
		this.loading$.pipe(takeWhile(() => this.isAlive)).subscribe(value => {
			this.isLoading = value;
		});

		this.resize$
			.pipe(takeWhile(() => this.isAlive))
			.subscribe();

	}

	ngOnDestroy() {
		this.isAlive = false;
	}

	closeModal() {
		this.modalService.close();
	}

	INDRA_handleSelectMonthClick( month ) {
		if (this.data && this.data[0] && this.data[0].handleClose) {
			this.data[0].handleClose(false, month);
			this.modalService.close();
		} else {
			this.modalService.close();
		}
	}

	buildMonthlist() {
		const routeType = this.INDRA_identifyRouteType(this.bookingSteps);
		if (this.currentStep == 0 && this.bookingSteps.length > 0 && !this.bookingSteps[this.currentStep].date_selected ) {
			this.calendarPriceService.getMonthPrice(this.bookingSteps, routeType, 0, this.isLoggedIn, this.priceUnit, this.paxType);
		} else if (this.bookingSteps[1] && !this.bookingSteps[1].date_selected) {
			this.calendarPriceService.getMonthPrice(this.bookingSteps, routeType, 1, this.isLoggedIn, this.priceUnit, this.paxType);
		} else {
			this.months = this.calendarPriceService.buildMonthlist(); // set months with only labels (no prices)
		}
	}
	
	INDRA_identifyRouteType(steps: BookingStep[]) {
		let tripType = TRIP_TYPE_ROUND_TRIP_CODE;
		if (steps.length == 1) {
			tripType = TRIP_TYPE_ONE_WAY_CODE;
		}
		return tripType;
	}

	INDRA_getMonthYear(date) {
		let month = moment(date).format('MMMM').toLowerCase();
		const year = moment(date).format('YYYY').toLowerCase();
		month = this.INDRA_getTranslationKey('dates.text.' + month);
		return month + ' ' + year;
	}

	INDRA_getTranslationKey(key: string): string {
		return this.ibeAdminService.getTranslationValue(key);
	}
}
